import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Home, Mail, Lock } from 'lucide-react';
import { tenantUrl } from '../../utils/tenantResolver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-1.55 4.3-3.74 4.25z"/></svg>
);

export default function PremiumLoginPage({ tenantConfig }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantSlug = tenantConfig?.slug || searchParams.get('tenant') || '';
  const tenantName = tenantConfig?.name || '';
  const logoUrl = tenantConfig?.logo_url || '';

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        ...form,
        tenant_slug: tenantSlug
      });
      localStorage.setItem('auth_token', res.data.token);
      localStorage.setItem('auth_user', JSON.stringify(res.data.user));
      localStorage.setItem('tenant_id', res.data.user.tenant_id);
      localStorage.setItem('tenant_slug', tenantSlug);
      toast.success(`Welcome back, ${res.data.user.name}!`);
      if (res.data.user.role === 'parent') {
        navigate(`/${tenantSlug}/parent`);
      } else {
        navigate(`/${tenantSlug}/dashboard`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const forgotUrl = tenantUrl('/forgot-password', tenantSlug);

  const handleSocialLogin = (provider) => {
    toast.info(`${provider} sign-in coming soon`);
  };

  return (
    <div className="prem-auth-page" data-testid="premium-login-page">
      <div className="prem-auth-window">
        {/* Browser Chrome */}
        <div className="prem-auth-chrome">
          <div className="prem-auth-dots">
            <span className="prem-dot prem-dot-red" />
            <span className="prem-dot prem-dot-yellow" />
            <span className="prem-dot prem-dot-green" />
          </div>
        </div>

        {/* Blue gradient header */}
        <div className="prem-auth-header-bar">
          <div className="prem-auth-brand-wrap">
            {logoUrl ? (
              <img src={logoUrl} alt={tenantName} className="prem-auth-brand-logo" />
            ) : (
              <div className="prem-auth-brand-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M5 3l14 9-14 9V3z"/></svg>
              </div>
            )}
            <span className="prem-auth-brand-text" data-testid="premium-login-brand">DepinaCRM</span>
          </div>
        </div>

        {/* White body */}
        <div className="prem-auth-body">
          <h1 className="prem-auth-welcome" data-testid="premium-login-welcome">Welcome Back!</h1>
          <p className="prem-auth-subtitle">Sign in to your account</p>

          {/* Tenant info box */}
          {tenantSlug && (
            <div className="prem-auth-tenant-box" data-testid="premium-login-tenant-badge">
              <div className="prem-auth-tenant-row prem-auth-tenant-domain">
                <Home size={18} />
                <span>{tenantSlug}.depinacrm.com</span>
              </div>
              <div className="prem-auth-tenant-divider" />
              <div className="prem-auth-tenant-row prem-auth-tenant-name">
                <Home size={16} />
                <span>{tenantName || tenantSlug}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="prem-auth-form" data-testid="premium-login-form">
            {/* Email */}
            <div className="prem-auth-field">
              <label>Email Address</label>
              <div className="prem-auth-input-wrap">
                <Mail size={18} className="prem-auth-input-icon" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="user@example.com"
                  autoComplete="email"
                  data-testid="premium-login-email"
                />
              </div>
            </div>

            {/* Password */}
            <div className="prem-auth-field">
              <div className="prem-auth-field-row">
                <label>Password</label>
                <Link to={forgotUrl} className="prem-auth-forgot" data-testid="premium-login-forgot-link">
                  Forgot Password?
                </Link>
              </div>
              <div className="prem-auth-input-wrap">
                <Lock size={18} className="prem-auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  data-testid="premium-login-password"
                />
                <button
                  type="button"
                  className="prem-auth-pw-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="premium-login-toggle-pw"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Sign In */}
            <button
              type="submit"
              className="prem-auth-submit"
              disabled={loading}
              data-testid="premium-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="prem-auth-divider">
            <span>or</span>
          </div>

          {/* Social buttons */}
          <div className="prem-auth-social">
            <button
              type="button"
              className="prem-auth-social-btn prem-auth-google"
              onClick={() => handleSocialLogin('Google')}
              data-testid="premium-login-google"
            >
              <GoogleIcon />
              Sign in with Google
            </button>
            <button
              type="button"
              className="prem-auth-social-btn prem-auth-apple"
              onClick={() => handleSocialLogin('Apple')}
              data-testid="premium-login-apple"
            >
              <AppleIcon />
              Sign in with Apple
            </button>
          </div>

          {/* Create account */}
          <p className="prem-auth-create">
            Don't have an account?{' '}
            <Link to={tenantUrl('/register', tenantSlug)} className="prem-auth-create-link" data-testid="premium-login-create">
              Create an Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
